/**
 * Note: Use position fixed according to your needs
 * Desktop navbar is better positioned at the bottom
 * Mobile navbar is better positioned at bottom right.
 **/

import { cn } from "../../lib/utils";
import { IconLayoutNavbarCollapse } from "@tabler/icons-react";
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from "motion/react";

import { useRef, useState } from "react";

// Add keyframes for the border animation
const borderKeyframes = `
@keyframes border-dance {
  0% {
    background-position: 0% 0%;
  }
  100% {
    background-position: 200% 0%;
  }
}

.animated-border {
  position: relative;
}

.animated-border::before {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: inherit;
  padding: 2px;
  background: linear-gradient(90deg, 
    rgba(255,165,0,0), 
    rgba(255,165,0,0.3), 
    rgba(255,165,0,0.8), 
    rgba(255,165,0,0.3), 
    rgba(255,165,0,0)
  );
  background-size: 200% 100%;
  animation: border-dance 4s linear infinite;
  -webkit-mask: 
    linear-gradient(#fff 0 0) content-box, 
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
}

.floating-dock-desktop {
  width: fit-content;
  min-width: 300px;
  max-width: 90vw;
}
`;

export const FloatingDock = ({
  items,
  desktopClassName,
  mobileClassName,
  activePath
}) => {
  return (
    <>
      <style>{borderKeyframes}</style>
      <div className="fixed inset-x-0 bottom-0 z-50 pointer-events-none">
        <div className="container mx-auto px-4 pb-4">
          <FloatingDockDesktop items={items} className={desktopClassName} activePath={activePath} />
          <FloatingDockMobile items={items} className={mobileClassName} activePath={activePath} />
        </div>
      </div>
    </>
  );
};

const FloatingDockMobile = ({
  items,
  className,
  activePath
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={cn("block md:hidden pointer-events-auto", className)}>
      <AnimatePresence>
        {open && (
          <motion.div
            layoutId="nav"
            className="absolute inset-x-0 bottom-full mb-2 flex flex-col gap-2">
            {items.map((item, idx) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 10 }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  y: 10,
                  transition: {
                    delay: idx * 0.05,
                  },
                }}
                transition={{ delay: (items.length - 1 - idx) * 0.05 }}>
                <button
                  onClick={(e) => {
                    setOpen(false);
                    item.onClick && item.onClick(e);
                  }}
                  className={cn(
                    "flex h-10 w-10 items-center justify-center rounded-full",
                    item.href === activePath 
                      ? "bg-orange-500/30 dark:bg-orange-500/30" 
                      : "bg-gray-50 dark:bg-neutral-900"
                  )}>
                  <div className={cn(
                    "h-4 w-4",
                    item.href === activePath ? "text-white" : "text-neutral-300"
                  )}>{item.icon}</div>
                </button>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
      <button
        onClick={() => setOpen(!open)}
        className="animated-border flex h-10 w-10 items-center justify-center rounded-full bg-gray-50 dark:bg-neutral-800">
        <IconLayoutNavbarCollapse className="h-5 w-5 text-neutral-500 dark:text-neutral-400" />
      </button>
    </div>
  );
};

const FloatingDockDesktop = ({
  items,
  className,
  activePath
}) => {
  let mouseX = useMotionValue(Infinity);
  return (
    <motion.div
      onMouseMove={(e) => mouseX.set(e.pageX)}
      onMouseLeave={() => mouseX.set(Infinity)}
      className={cn(
        "animated-border floating-dock-desktop hidden h-16 items-end gap-4 rounded-2xl bg-gray-50 px-4 pb-3 md:flex dark:bg-neutral-900 pointer-events-auto",
        className
      )}>
      {items.map((item) => (
        <IconContainer 
          mouseX={mouseX} 
          key={item.title} 
          isActive={item.href === activePath}
          {...item} 
        />
      ))}
    </motion.div>
  );
};

function IconContainer({
  mouseX,
  title,
  icon,
  href,
  onClick,
  isActive
}) {
  let ref = useRef(null);

  let distance = useTransform(mouseX, (val) => {
    let bounds = ref.current?.getBoundingClientRect() ?? { x: 0, width: 0 };
    return val - bounds.x - bounds.width / 2;
  });

  let widthTransform = useTransform(distance, [-150, 0, 150], [40, 80, 40]);
  let heightTransform = useTransform(distance, [-150, 0, 150], [40, 80, 40]);

  let widthTransformIcon = useTransform(distance, [-150, 0, 150], [20, 40, 20]);
  let heightTransformIcon = useTransform(distance, [-150, 0, 150], [20, 40, 20]);

  let width = useSpring(widthTransform, {
    mass: 0.1,
    stiffness: 150,
    damping: 12,
  });
  let height = useSpring(heightTransform, {
    mass: 0.1,
    stiffness: 150,
    damping: 12,
  });

  let widthIcon = useSpring(widthTransformIcon, {
    mass: 0.1,
    stiffness: 150,
    damping: 12,
  });
  let heightIcon = useSpring(heightTransformIcon, {
    mass: 0.1,
    stiffness: 150,
    damping: 12,
  });

  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="relative">
      <motion.div
        ref={ref}
        style={{ width, height }}
        className={cn(
          "flex aspect-square items-center justify-center rounded-full",
          isActive ? "bg-orange-500/30 dark:bg-orange-500/30" : "bg-gray-200 dark:bg-neutral-800"
        )}>
        <AnimatePresence>
          {hovered && (
            <motion.div
              initial={{ opacity: 0, y: 10, x: "-50%" }}
              animate={{ opacity: 1, y: 0, x: "-50%" }}
              exit={{ opacity: 0, y: 2, x: "-50%" }}
              className="absolute -top-8 left-1/2 w-fit rounded-md border border-gray-200 bg-gray-100 px-2 py-0.5 text-xs whitespace-pre text-neutral-700 dark:border-neutral-900 dark:bg-neutral-800 dark:text-white">
              {title}
            </motion.div>
          )}
        </AnimatePresence>
        <motion.div
          style={{ width: widthIcon, height: heightIcon }}
          className="flex items-center justify-center">
          <div className={cn(
            "w-full h-full",
            isActive ? "text-white" : "text-neutral-300"
          )}>
            {icon}
          </div>
        </motion.div>
      </motion.div>
    </button>
  );
}
